import { useEffect, useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Map } from 'mapbox-gl';

/**
 * URL State Synchronization Hook
 *
 * Manages bidirectional sync between map zoom/center and URL query parameters.
 * - Reads ?zoom=14.5&lng=...&lat=... on mount
 * - Updates URL when map zoom/move changes (debounced)
 * - Manages ?cluster=lng,lat parameter for shareable cluster views
 * - Persists viewport to sessionStorage for refresh persistence
 */

// Session storage key for viewport persistence (cleared on logout)
const MAP_VIEWPORT_STORAGE_KEY = 'map-viewport';

interface StoredViewport {
  zoom: number;
  lng: number;
  lat: number;
}

/**
 * Clear stored viewport from sessionStorage (call on logout)
 */
export function clearStoredViewport(): void {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(MAP_VIEWPORT_STORAGE_KEY);
  }
}

/**
 * Get stored viewport from sessionStorage (exported for MapView to skip fitBounds when restoring)
 */
export function getStoredViewport(): StoredViewport | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(MAP_VIEWPORT_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as StoredViewport;
    // Validate the stored values
    if (
      typeof parsed.zoom === 'number' &&
      typeof parsed.lng === 'number' &&
      typeof parsed.lat === 'number' &&
      parsed.zoom >= 0 && parsed.zoom <= 22 &&
      (Math.abs(parsed.lng) > 1 || Math.abs(parsed.lat) > 1) // Not near 0,0
    ) {
      return parsed;
    }
  } catch {
    // Invalid stored data, ignore
  }
  return null;
}

/**
 * Save viewport to sessionStorage
 */
function saveViewportToStorage(viewport: StoredViewport): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(MAP_VIEWPORT_STORAGE_KEY, JSON.stringify(viewport));
  } catch {
    // Storage error, ignore
  }
}

interface UseUrlStateProps {
  map: Map | null;
  mapLoaded: boolean;
}

  interface UseUrlStateReturn {
    syncZoomToUrl: (zoom: number) => void;
    syncViewportToUrl: () => void;
    syncClusterToUrl: (coordinates: [number, number] | null) => void;
    getShareableUrl: (extraParams?: Record<string, string>) => string;
    initialClusterCoords: [number, number] | null;
  }
  
  const URL_UPDATE_DEBOUNCE_MS = 300;
  
  export function useUrlState({ map, mapLoaded }: UseUrlStateProps): UseUrlStateReturn {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const viewportSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const initialUrlStateApplied = useRef(false);
    const [initialClusterCoords, setInitialClusterCoords] = useState<[number, number] | null>(null);
  
    // Read state from URL on mount (with sessionStorage fallback)
  useEffect(() => {
    // Only run once to parse initial params
    if (initialUrlStateApplied.current) return;

    // Parse cluster coordinates first so they are available immediately
    const clusterParam = searchParams.get('cluster');
    if (clusterParam) {
      const [lng, lat] = clusterParam.split(',').map(Number);
      if (!isNaN(lng) && !isNaN(lat)) {
        setInitialClusterCoords([lng, lat]);
      }
    }

    if (!map || !mapLoaded) return;

    const urlZoom = searchParams.get('zoom');
    const urlLng = searchParams.get('lng');
    const urlLat = searchParams.get('lat');

    // Check if URL has viewport params
    const hasUrlViewport = urlZoom || (urlLng && urlLat);

    // If URL has viewport params, use them
    if (hasUrlViewport) {
      // Apply zoom if present
      if (urlZoom) {
        const zoom = parseFloat(urlZoom);
        if (!isNaN(zoom) && zoom >= 0 && zoom <= 22) {
          map.setZoom(zoom);
        }
      }

      // Apply center coordinates if both present and valid
      // Reject coordinates near 0,0 (ocean off Africa - not a valid truck location)
      if (urlLng && urlLat) {
        const lng = parseFloat(urlLng);
        const lat = parseFloat(urlLat);
        const isValidCoord = !isNaN(lng) && !isNaN(lat) &&
                             (Math.abs(lng) > 1 || Math.abs(lat) > 1); // Not near 0,0
        if (isValidCoord) {
          map.setCenter([lng, lat]);
        }
      }
    } else {
      // No URL viewport params - try sessionStorage for refresh persistence
      const storedViewport = getStoredViewport();
      if (storedViewport) {
        map.setZoom(storedViewport.zoom);
        map.setCenter([storedViewport.lng, storedViewport.lat]);
      }
    }

    initialUrlStateApplied.current = true;
  }, [map, mapLoaded, searchParams]);

  // Debounced function to update URL with current zoom
  const syncZoomToUrl = useCallback((zoom: number) => {
    // Clear any pending URL update
    if (urlSyncTimeoutRef.current) {
      clearTimeout(urlSyncTimeoutRef.current);
    }

    // Schedule URL update with debounce
    urlSyncTimeoutRef.current = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          // Round to whole numbers for cleaner URLs
          newParams.set('zoom', Math.round(zoom).toString());
          return newParams;
        },
        { replace: true } // Replace to avoid cluttering browser history
      );
      urlSyncTimeoutRef.current = null;
    }, URL_UPDATE_DEBOUNCE_MS);
  }, [setSearchParams]);

  // Debounced function to sync full viewport (center + zoom) to URL and sessionStorage
  const syncViewportToUrl = useCallback(() => {
    if (!map) return;

    // Clear any pending viewport update
    if (viewportSyncTimeoutRef.current) {
      clearTimeout(viewportSyncTimeoutRef.current);
    }

    // Schedule viewport update with debounce
    viewportSyncTimeoutRef.current = setTimeout(() => {
      const center = map.getCenter();
      const zoom = map.getZoom();

      // Save to sessionStorage for refresh persistence
      saveViewportToStorage({
        zoom: Math.round(zoom),
        lng: parseFloat(center.lng.toFixed(6)),
        lat: parseFloat(center.lat.toFixed(6)),
      });

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('zoom', Math.round(zoom).toString());
          newParams.set('lng', center.lng.toFixed(6));
          newParams.set('lat', center.lat.toFixed(6));
          return newParams;
        },
        { replace: true }
      );
      viewportSyncTimeoutRef.current = null;
    }, URL_UPDATE_DEBOUNCE_MS);
  }, [map, setSearchParams]);

  // Update cluster URL param
  const syncClusterToUrl = useCallback((coordinates: [number, number] | null) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (coordinates) {
          newParams.set('cluster', `${coordinates[0].toFixed(6)},${coordinates[1].toFixed(6)}`);
        } else {
          newParams.delete('cluster');
        }
        return newParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Generate a shareable URL with current map state + extra params
  const getShareableUrl = useCallback((extraParams?: Record<string, string>) => {
    const url = new URL(window.location.href);
    
    // Ensure viewport params are up-to-date from map instance if available
    if (map) {
      const center = map.getCenter();
      const zoom = map.getZoom();
      url.searchParams.set('zoom', Math.round(zoom).toString());
      url.searchParams.set('lng', center.lng.toFixed(6));
      url.searchParams.set('lat', center.lat.toFixed(6));
    }

    // Apply extra params (e.g. cluster, unit)
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          url.searchParams.delete(key);
        } else {
          url.searchParams.set(key, value);
        }
      });
    }

    return url.toString();
  }, [map]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (urlSyncTimeoutRef.current) {
        clearTimeout(urlSyncTimeoutRef.current);
      }
      if (viewportSyncTimeoutRef.current) {
        clearTimeout(viewportSyncTimeoutRef.current);
      }
    };
  }, []);

  return { syncZoomToUrl, syncViewportToUrl, syncClusterToUrl, getShareableUrl, initialClusterCoords };
}
